import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import palette from 'core-system/Themes/palette'
import { Flex, Micromobility } from 'core-system/Icons/Sidebar'
import { Car } from 'core-system/Icons/modeTypes'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import { dashboardActions } from './dashboardActions'
import { DashboardLocationProgram } from './dashboardTypes'

const Container = styled.div`
  padding: 2rem;
`

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1rem;
`

const BreadcrumbLink = styled(Link)`
  color: ${palette.primary.pink700};
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 1.5rem;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${palette.grey.grey200};
  margin-bottom: 1.5rem;
`

const Tab = styled.div`
  padding: 0.75rem 0;
  margin-right: 1.5rem;
  border-bottom: 2px solid ${palette.primary.pink700};
  color: ${palette.text.primary};
  font-weight: 600;
  font-size: 0.875rem;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

const ProgramList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ProgramCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  flex-wrap: wrap;
`

const IconCircle = styled.div<{ $bg: string }>`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: ${(props) => props.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${palette.white};

  svg {
    width: 1.35rem;
    height: 1.35rem;
  }
`

const ProgramInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 10rem;
  flex: 1 1 12rem;
`

const EnrollmentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 10rem;
  flex: 1 1 12rem;
`

const ProgressTrack = styled.div`
  height: 0.375rem;
  width: 100%;
  max-width: 12rem;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  overflow: hidden;
`

const ProgressFill = styled.div<{ rate: number }>`
  height: 100%;
  width: ${({ rate }) => Math.min(Math.max(rate, 0), 100)}%;
  border-radius: 999px;
  background: ${palette.primary.pink700};
`

const SpendBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 7rem;
  text-align: right;
`

const EmptyState = styled.div`
  padding: 2rem;
  border: 1px dashed ${palette.grey.grey300};
  border-radius: 0.75rem;
  text-align: center;
`

const formatCount = (value: number) => value.toLocaleString('en-US')

const formatMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

const PROGRAM_ICON: Record<string, { bg: string; icon: React.ReactNode }> = {
  FLEX: {
    bg: palette.secondary.purple500,
    icon: <Flex />,
  },
  TRANSIT_PASS: {
    bg: palette.accent.teal500,
    icon: <CreditCard />,
  },
  MICROMOBILITY: {
    bg: palette.accent.blue500,
    icon: <Micromobility />,
  },
  GRH: {
    bg: palette.warning.yellow600,
    icon: <Car />,
  },
  OTP: {
    bg: palette.primary.pink500,
    icon: <CreditCard />,
  },
}

const getProgramVisual = (types: string[]) => {
  for (const type of [
    'TRANSIT_PASS',
    'FLEX',
    'MICROMOBILITY',
    'GRH',
    'OTP',
  ]) {
    if (types.includes(type) && PROGRAM_ICON[type]) {
      return PROGRAM_ICON[type]
    }
  }
  return {
    bg: palette.grey.grey500,
    icon: <CreditCard />,
  }
}

const LocationProgramRow = ({
  program,
  onManage,
}: {
  program: DashboardLocationProgram
  onManage: () => void
}) => {
  const visual = getProgramVisual(program.types)
  const delta = program.enrolledDelta
  const deltaLabel =
    delta > 0
      ? `+${delta} this month`
      : delta < 0
        ? `${delta} this month`
        : '0 this month'

  return (
    <ProgramCard data-cy='location-program-row'>
      <IconCircle $bg={visual.bg}>{visual.icon}</IconCircle>
      <ProgramInfo>
        <Text variant='action2' fontWeight={600}>
          {program.superProgramName}
        </Text>
        <Text variant='caption' color={palette.text.secondary}>
          {program.subtitle}
        </Text>
      </ProgramInfo>
      <EnrollmentBlock>
        <Text variant='body2' color={palette.text.secondary}>
          {formatCount(program.enrolled)} of{' '}
          {formatCount(program.employeeCount)} enrolled
        </Text>
        <ProgressTrack>
          <ProgressFill rate={program.utilizationRate} />
        </ProgressTrack>
        <Text variant='caption' color={palette.text.secondary}>
          {program.utilizationRate}%
        </Text>
      </EnrollmentBlock>
      <SpendBlock>
        <Text variant='action2' fontWeight={600}>
          {formatMoney(program.moneySpentCents)}
        </Text>
        <Text
          variant='caption'
          color={
            delta > 0
              ? palette.success.green600
              : delta < 0
                ? palette.error.red600
                : palette.text.secondary
          }
        >
          {deltaLabel}
        </Text>
      </SpendBlock>
      <SecondaryButton onClick={onManage}>Manage</SecondaryButton>
    </ProgramCard>
  )
}

const LocationDetailView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { locationKey: rawLocationKey } = useParams<{ locationKey: string }>()
  const locationKey = rawLocationKey
    ? decodeURIComponent(rawLocationKey)
    : ''

  const { locationDetail, locationDetailLoading, locationDetailError } =
    useSelector((state: AppState) => state.dashboard)

  useEffect(() => {
    if (locationKey) {
      dispatch(dashboardActions.getDashboardLocationDetail(locationKey))
    }
    return () => {
      dispatch(dashboardActions.clearDashboardLocationDetail())
    }
  }, [dispatch, locationKey])

  if (!locationDetail) {
    return (
      <Container>
        <EmptyState>
          <Text variant='body1' color={palette.text.secondary}>
            {locationDetailError
              ? 'Location not found'
              : locationDetailLoading || locationKey
                ? 'Loading location…'
                : 'Location not found'}
          </Text>
        </EmptyState>
      </Container>
    )
  }

  const goPrograms = () => navigate(Locations.Programs.Overview)
  const goLaunch = () => navigate(Locations.Programs.Launch)

  return (
    <Container data-cy='location-detail-view'>
      <Breadcrumb>
        <BreadcrumbLink to={Locations.Dashboard}>Dashboard</BreadcrumbLink>
        <Text variant='caption' color={palette.text.secondary}>
          /
        </Text>
        <Text variant='caption' color={palette.text.secondary}>
          {locationDetail.city}
        </Text>
      </Breadcrumb>

      <HeaderRow>
        <HeaderLeft>
          <Text variant='h1' fontWeight={600}>
            {locationDetail.city}
          </Text>
        </HeaderLeft>
      </HeaderRow>

      <StatsRow>
        <Stat>
          <Text variant='h2' fontWeight={600}>
            {formatCount(locationDetail.employeeCount)}
          </Text>
          <Text variant='caption' color={palette.text.secondary}>
            Employees
          </Text>
        </Stat>
        <Stat>
          <Text variant='h2' fontWeight={600}>
            {formatCount(locationDetail.enrolled)}
          </Text>
          <Text variant='caption' color={palette.text.secondary}>
            Enrolled
          </Text>
        </Stat>
        <Stat>
          <Text variant='h2' fontWeight={600}>
            {locationDetail.enrollmentRate}%
          </Text>
          <Text variant='caption' color={palette.text.secondary}>
            Participation
          </Text>
        </Stat>
      </StatsRow>

      <Tabs>
        <Tab>Programs</Tab>
      </Tabs>

      <SectionHeader>
        <Text variant='h3' fontWeight={500}>
          Active Programs
        </Text>
        <PrimaryButton onClick={goLaunch}>+ Add Program</PrimaryButton>
      </SectionHeader>

      {locationDetail.programs.length > 0 ? (
        <ProgramList>
          {locationDetail.programs.map((program) => (
            <LocationProgramRow
              key={program.launchId}
              program={program}
              onManage={goPrograms}
            />
          ))}
        </ProgramList>
      ) : (
        <EmptyState>
          <Text variant='body1' color={palette.text.secondary}>
            No active programs for employees at this location
          </Text>
        </EmptyState>
      )}
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  LocationDetailView.displayName = 'LocationDetailView'
}

export default LocationDetailView
